import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import Select from '../Select';

export const BLOCK_TYPES = ['p', 'h1', 'h2', 'h3', 'bullet', 'number', 'todo', 'quote'];
const LIST_TYPES = ['bullet', 'number', 'todo'];

// Scorciatoie in stile markdown: scrivere il prefisso all'inizio di un paragrafo ne cambia il tipo.
const SHORTCUTS = [
  [/^### /, 'h3'],
  [/^## /, 'h2'],
  [/^# /, 'h1'],
  [/^[-*] /, 'bullet'],
  [/^1\. /, 'number'],
  [/^\[\] /, 'todo'],
  [/^> /, 'quote'],
];

const newId = () => Math.random().toString(36).slice(2, 8);

function BlockInput({ block, index, number, placeholder, onChange, onKeyDown, setRef }) {
  const { t } = useLanguage();
  const ref = useRef(null);

  // Il campo cresce con il testo (niente barra di scorrimento interna).
  useLayoutEffect(() => {
    const el = ref.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [block.text]);

  return (
    <div className={`be-block be-${block.type}`}>
      <Select
        className="be-type"
        value={block.type}
        aria-label={t('projectDetail.blockType')}
        onChange={(e) => onChange({ ...block, type: e.target.value })}
      >
        {BLOCK_TYPES.map((type) => <option key={type} value={type}>{t(`projectDetail.block_${type}`)}</option>)}
      </Select>
      {block.type === 'bullet' && <span className="be-marker" aria-hidden="true">•</span>}
      {block.type === 'number' && <span className="be-marker" aria-hidden="true">{number}.</span>}
      {block.type === 'todo' && (
        <input type="checkbox" className="be-check" checked={Boolean(block.checked)} aria-label={t('projectDetail.blockDone')} onChange={(e) => onChange({ ...block, checked: e.target.checked })} />
      )}
      <textarea
        ref={(el) => {
          ref.current = el;
          setRef(block.id, el);
        }}
        className={'be-input' + (block.type === 'todo' && block.checked ? ' be-input-done' : '')}
        rows={1}
        value={block.text}
        placeholder={placeholder}
        aria-label={`${t('projectDetail.blockText')} ${index + 1}`}
        onChange={(e) => onChange({ ...block, text: e.target.value })}
        onKeyDown={(e) => onKeyDown(e, block, index)}
      />
    </div>
  );
}

/**
 * Editor a blocchi per il corpo di un'attività: paragrafi, titoli, elenchi,
 * caselle da spuntare e citazioni. Solo testo semplice (nessun HTML): si
 * invia come elenco di blocchi {id, type, text, checked}. Invio crea un nuovo
 * blocco, Backspace a inizio riga ne toglie il tipo o lo unisce al precedente.
 */
function BlockEditor({ blocks, onChange }) {
  const { t } = useLanguage();
  const refs = useRef({});
  const [focusReq, setFocusReq] = useState(null); // {id, pos}

  useEffect(() => {
    if (!focusReq) return;
    const el = refs.current[focusReq.id];
    if (el) {
      el.focus();
      const pos = focusReq.pos === 'end' ? el.value.length : focusReq.pos;
      el.setSelectionRange(pos, pos);
    }
    setFocusReq(null);
  }, [focusReq]);

  const items = blocks.length > 0 ? blocks : [{ id: 'placeholder', type: 'p', text: '' }];

  const replace = (id, next) => {
    // Primo carattere nel campo vuoto: il blocco segnaposto diventa un blocco vero.
    const base = blocks.length > 0 ? blocks : [{ ...next, id: newId() }];
    let updated = blocks.length > 0 ? blocks.map((b) => (b.id === id ? next : b)) : base;
    // Scorciatoie markdown su un paragrafo.
    const target = updated.find((b) => b.id === (blocks.length > 0 ? id : base[0].id));
    if (target && target.type === 'p') {
      const hit = SHORTCUTS.find(([re]) => re.test(target.text));
      if (hit) {
        updated = updated.map((b) => (b.id === target.id ? { ...b, type: hit[1], text: b.text.replace(hit[0], '') } : b));
      }
    }
    onChange(updated);
    if (blocks.length === 0) setFocusReq({ id: updated[0].id, pos: 'end' });
  };

  const onKeyDown = (e, block, index) => {
    const el = e.target;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    if (blocks.length === 0) return;

    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      // Elemento di elenco vuoto: Invio esce dall'elenco.
      if (LIST_TYPES.includes(block.type) && block.text === '') {
        onChange(blocks.map((b) => (b.id === block.id ? { ...b, type: 'p', checked: undefined } : b)));
        return;
      }
      const created = { id: newId(), type: LIST_TYPES.includes(block.type) ? block.type : 'p', text: block.text.slice(end) };
      const next = [...blocks];
      next[index] = { ...block, text: block.text.slice(0, start) };
      next.splice(index + 1, 0, created);
      onChange(next);
      setFocusReq({ id: created.id, pos: 0 });
    } else if (e.key === 'Backspace' && start === 0 && end === 0) {
      if (block.type !== 'p') {
        e.preventDefault();
        onChange(blocks.map((b) => (b.id === block.id ? { ...b, type: 'p', checked: undefined } : b)));
      } else if (index > 0) {
        e.preventDefault();
        const prev = blocks[index - 1];
        const merged = blocks.filter((b) => b.id !== block.id).map((b) => (b.id === prev.id ? { ...b, text: b.text + block.text } : b));
        onChange(merged);
        setFocusReq({ id: prev.id, pos: prev.text.length });
      }
    } else if (e.key === 'ArrowUp' && index > 0 && !el.value.slice(0, start).includes('\n')) {
      e.preventDefault();
      setFocusReq({ id: blocks[index - 1].id, pos: 'end' });
    } else if (e.key === 'ArrowDown' && index < blocks.length - 1 && !el.value.slice(end).includes('\n')) {
      e.preventDefault();
      setFocusReq({ id: blocks[index + 1].id, pos: 0 });
    }
  };

  // Numerazione: conta i blocchi "number" consecutivi.
  let counter = 0;
  const numbers = items.map((b) => {
    counter = b.type === 'number' ? counter + 1 : 0;
    return counter;
  });

  return (
    <div className="be-editor">
      {items.map((block, index) => (
        <BlockInput
          key={block.id}
          block={block}
          index={index}
          number={numbers[index]}
          placeholder={blocks.length === 0 ? t('projectDetail.bodyPlaceholder') : ''}
          onChange={(next) => replace(block.id, next)}
          onKeyDown={onKeyDown}
          setRef={(id, el) => { refs.current[id] = el; }}
        />
      ))}
    </div>
  );
}

export default BlockEditor;
